"use client"

import { useState } from "react"
import { Button, Input, Label } from "@ui/shad"
import { AlertCircle, Loader2 } from "lucide-react"

import { useUpdateDataImportSourceCredentials } from "@/hooks/api/data-import-sources"

interface CredentialReEntryProps {
  sourceId: string
  sourceName: string
  errorMessage?: string
  initialUsername?: string
}

export function CredentialReEntry({ sourceId, sourceName, errorMessage, initialUsername }: CredentialReEntryProps) {
  const [crmUsername, setCrmUsername] = useState(initialUsername ?? "")
  const [crmPassword, setCrmPassword] = useState("")
  const updateCredentials = useUpdateDataImportSourceCredentials(sourceId)

  const canSubmit = crmUsername.trim().length > 0 && crmPassword.length > 0 && !updateCredentials.isPending

  const handleSubmit = () => {
    if (!canSubmit) return
    updateCredentials.mutate({ crmUsername, crmPassword })
  }

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-6 py-6">
      <div className="bg-yellow-background border-yellow-border text-yellow-foreground flex items-start gap-3 rounded-lg border p-4">
        <AlertCircle className="mt-0.5 size-5 shrink-0" />
        <div>
          <h3 className="text-sm font-medium">Login didn&apos;t work</h3>
          <p className="mt-1 text-sm">
            {errorMessage ||
              `We couldn't sign in to ${sourceName}. Double-check your username and password and try again.`}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="re-entry-username">{sourceName} username</Label>
          <Input
            id="re-entry-username"
            type="text"
            placeholder={`Enter your ${sourceName} username`}
            value={crmUsername}
            onChange={(e) => setCrmUsername(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="re-entry-password">{sourceName} password</Label>
          <Input
            id="re-entry-password"
            type="password"
            placeholder={`Enter your ${sourceName} password`}
            value={crmPassword}
            onChange={(e) => setCrmPassword(e.target.value)}
          />
        </div>
        <p className="text-muted-foreground text-xs">
          Credentials are stored encrypted and only used to fetch your {sourceName} workflows.
        </p>
      </div>

      <Button onClick={handleSubmit} disabled={!canSubmit}>
        {updateCredentials.isPending ? (
          <>
            <Loader2 className="animate-spin" />
            Retrying
          </>
        ) : (
          "Retry sign-in"
        )}
      </Button>
    </div>
  )
}
